import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Table, Segmented } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import AnalyticsPage from '../../shared-components/analytics/AnalyticsPage';
import QualityNote from '../../shared-components/analytics/QualityNote';
import StatTile from '../../shared-components/analytics/StatTile';
import ShareBar from '../../shared-components/charts/ShareBar';
import { getOrderMetrics } from '../../../analytics/analyticsApi';
import { thousand, toCurrency } from '../../../functions';

const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(2)}%`);

function AnalyticsTender() {
  const { t } = useTranslation();
  const [groupBy, setGroupBy] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // completedSalesOnly stays false: leaving it on removes cancelled orders
    // from the denominator and the cancellation rate comes back a flat zero.
    getOrderMetrics({ groupBy, completedSalesOnly: false })
      .then((d) => { setResult(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [groupBy]);

  const rows = result?.items ?? [];
  const orders = _.sumBy(rows, 'orderCount');
  const revenue = _.sumBy(rows, 'productsTotal');
  const cancelled = _.sumBy(rows, 'cancelledOrderCount');
  const cash = _.find(rows, (r) => r.name === 'Cash');

  return (
    <AnalyticsPage
      title={t('analytics_tender_title')}
      subtitle={t('analytics_tender_subtitle')}
    >
      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label={t('analytics_orders')} value={thousand(orders)} emphasis />
        <StatTile
          label={t('analytics_avg_basket')}
          value={toCurrency(orders > 0 ? revenue / orders : null)}
          hint={t('analytics_avg_basket_help')}
        />
        <StatTile
          label={t('analytics_cash_share')}
          value={pct(cash?.shareOfRevenuePct)}
          sub={cash ? toCurrency(cash.productsTotal) : null}
          hint={t('analytics_cash_help')}
        />
        <StatTile
          label={t('analytics_cancellation_rate')}
          value={pct(orders > 0 ? (cancelled / orders) * 100 : null)}
          hint={t('analytics_cancellation_help')}
        />
      </div>

      <div className="mb-5 rounded-card border border-info/30 bg-teal-50 px-4 py-3">
        <div className="flex items-start gap-3">
          <FontAwesomeIcon icon={faCircleInfo} className="mt-0.5 text-info" />
          <div className="text-xs leading-relaxed text-ink">
            {t('analytics_cash_first_time')}
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-card border border-line bg-surface p-4 shadow-card">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm font-semibold text-ink">{t('analytics_revenue_mix')}</span>
            <Segmented
              size="small"
              value={groupBy}
              onChange={setGroupBy}
              options={[
                { label: t('analytics_tender'), value: 0 },
                { label: t('analytics_lane_kind'), value: 3 },
                { label: t('analytics_store'), value: 5 },
              ]}
            />
          </div>
          <ShareBar
            rows={rows}
            labelKey="name"
            valueKey="productsTotal"
            formatValue={(v) => toCurrency(v)}
          />
        </div>

        <div className="rounded-card border border-line bg-surface p-4 shadow-card">
          <div className="mb-3 text-sm font-semibold text-ink">{t('analytics_by_tender')}</div>
          <Table
            size="small"
            bordered={false}
            loading={loading}
            rowKey="key"
            pagination={false}
            scroll={{ x: 'max-content' }}
            dataSource={rows}
            locale={{ emptyText: t('no_data') }}
            columns={[
              {
                title: t('name'),
                key: 'name',
                render: (r) => <span className="text-sm font-semibold text-ink">{r.name}</span>,
              },
              {
                title: t('analytics_orders'),
                key: 'orders',
                align: 'right',
                render: (r) => <span className="tabular-nums">{thousand(r.orderCount)}</span>,
              },
              {
                title: t('analytics_revenue'),
                key: 'revenue',
                align: 'right',
                render: (r) => <span className="tabular-nums">{toCurrency(r.productsTotal)}</span>,
              },
              {
                title: t('analytics_avg_basket'),
                key: 'basket',
                align: 'right',
                render: (r) => <span className="tabular-nums">{toCurrency(r.averageBasketValue)}</span>,
              },
              {
                title: t('analytics_cancellation_rate'),
                key: 'cancel',
                align: 'right',
                render: (r) => <span className="tabular-nums text-muted">{pct(r.cancellationRatePct)}</span>,
              },
            ]}
            summary={() => (
              <Table.Summary.Row className="bg-gold-50 font-semibold">
                <Table.Summary.Cell index={0}>{t('total')}</Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <span className="tabular-nums">{thousand(orders)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <span className="tabular-nums">{toCurrency(revenue)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <span className="tabular-nums">{toCurrency(orders > 0 ? revenue / orders : null)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <span className="tabular-nums">{pct(orders > 0 ? (cancelled / orders) * 100 : null)}</span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
          <QualityNote quality={result?.quality} className="mt-4 border-t border-line pt-3" />
        </div>
      </div>
    </AnalyticsPage>
  );
}

export default AnalyticsTender;
